// Sustainability — Public SEO surface for Canadian landlords.
// Hub: /sustainability   ·   Province page + lead-magnet: /sustainability/:province
import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Leaf, ArrowRight, MapPin, Sparkles, Loader2, CheckCircle2,
  AlertCircle, TrendingUp, Building2,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { useBotShield, BotHoneypot } from "@/lib/botShield";

function useMeta({ title, description, canonical }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.setAttribute("name","description"); document.head.appendChild(m); }
      m.setAttribute("content", description);
    }
    if (canonical) {
      let l = document.querySelector('link[rel="canonical"]');
      if (!l) { l = document.createElement("link"); l.setAttribute("rel","canonical"); document.head.appendChild(l); }
      l.setAttribute("href", canonical);
    }
  }, [title, description, canonical]);
}

function JsonLd({ data }) {
  return <script type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}/>;
}

// ────────────────────────────────────────────────────────────────────────
// /sustainability hub
// ────────────────────────────────────────────────────────────────────────
export default function SustainabilityHub() {
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);

  useMeta({
    title: "Sustainability + Energy Benchmarking for Canadian Landlords | Central Rentals",
    description: "Province-by-province guide to building energy benchmarking, sustainability scoring, and retrofit ROI for Canadian rental property landlords. Free Health Check inside.",
    canonical: "https://centralrentals.ca/sustainability",
  });

  useEffect(() => {
    fetch(`/api/sustainability/manifest`)
      .then((r) => r.json())
      .then((d) => setProvinces(d.provinces || []))
      .catch(() => setProvinces([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="sustainability-hub">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Central Rentals Sustainability OS",
        serviceType: "Building energy benchmarking & sustainability advisory",
        areaServed: { "@type": "Country", name: "Canada" },
        provider: { "@type": "Organization", name: "Central Rentals Canada", url: "https://centralrentals.ca" },
      }}/>
      <PublicHeader/>

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700 inline-flex items-center gap-1.5">
            <Leaf className="w-3 h-3"/> Free Health Check inside
          </div>
          <h1 className="font-display mt-3 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] max-w-3xl">
            The Canadian landlord&apos;s <span className="text-emerald-700">sustainability OS</span>.
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed">
            Energy benchmarking, sustainability scoring, and retrofit ROI — calibrated for every province. Mark scores your building in 30 seconds and tells you exactly which retrofit pays back fastest.
          </p>
        </div>
      </section>

      {/* Province grid */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-emerald-50/40 via-white to-sky-50/40 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">By province</div>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
            Your regulatory landscape, by province
          </h2>
          {loading && (
            <div className="mt-8 py-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading provinces…
            </div>
          )}
          {!loading && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sustainability-hub-provinces">
              {provinces.map((p) => (
                <Link key={p.slug} to={p.page_url} data-testid={`sustainability-hub-province-${p.slug}`}
                  className="group p-5 rounded-2xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none font-bold text-sm">
                    {p.code}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      <MapPin className="w-3 h-3"/> {p.name}
                    </div>
                    <div className="font-display text-base font-bold text-slate-900 mt-1 leading-snug truncate">
                      Sustainability in {p.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5">
                      Grid: {(p.grid_factor_kg_kwh * 1000).toFixed(0)} g CO₂/kWh · {p.regulation_count} regs
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-emerald-600 flex-none opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all mt-1"/>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Building2 className="w-10 h-10 mx-auto text-emerald-400"/>
          <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
            30 seconds for a free sustainability score.
          </h2>
          <p className="mt-3 text-slate-300">Pick your province above to start.</p>
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// /sustainability/:province
// ────────────────────────────────────────────────────────────────────────
export function SustainabilityProvince() {
  const { province } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`/api/sustainability/${province}/content`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { if (!cancelled) setContent(d); })
      .catch((e) => { if (!cancelled) setError(String(e?.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [province]);

  const provinceName = content?.province_name || (province || "").replace(/-/g, " ");
  useMeta({
    title: content?.hero_headline
      ? `${content.hero_headline} | ${provinceName} | Central Rentals`
      : `Sustainability in ${provinceName} | Central Rentals`,
    description: content?.intro_paragraph || "Energy benchmarking, sustainability scoring, and retrofit ROI for Canadian rental landlords.",
    canonical: `https://centralrentals.ca/sustainability/${province}`,
  });

  if (error && !loading) return <Navigate to="/sustainability" replace/>;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="sustainability-province">
      {content && (
        <JsonLd data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Sustainability — ${content.province_name}`,
              serviceType: "Building sustainability advisory",
              areaServed: { "@type": "AdministrativeArea", name: content.province_name },
              description: content.intro_paragraph,
            },
            {
              "@type": "FAQPage",
              mainEntity: (content.faqs || []).map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}/>
      )}
      <PublicHeader/>

      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Link to="/sustainability" data-testid="sustainability-province-back"
            className="text-xs text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 font-bold">
            <ArrowRight className="w-3 h-3 rotate-180"/> All provinces
          </Link>
          {loading ? (
            <div className="mt-6 py-12 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading {provinceName}…
            </div>
          ) : content && (
            <>
              <div className="mt-4 text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700 inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3"/> {content.province_name}
              </div>
              <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05] max-w-3xl" data-testid="sustainability-headline">
                {content.hero_headline}
              </h1>
              <p className="mt-4 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed">
                {content.hero_sub}
              </p>
            </>
          )}
        </div>
      </section>

      {content && !loading && (
        <>
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-3xl mx-auto px-5 md:px-8">
              <p className="text-base md:text-lg text-slate-700 leading-relaxed">{content.intro_paragraph}</p>
            </div>
          </section>

          {/* Lead-magnet Health Check */}
          <HealthCheckPane province={province} provinceName={content.province_name}/>

          {/* Why now */}
          {Array.isArray(content.why_now) && content.why_now.length > 0 && (
            <section className="py-10 md:py-14 bg-gradient-to-br from-emerald-50/40 to-white border-y border-slate-200">
              <div className="max-w-4xl mx-auto px-5 md:px-8">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Why now</div>
                <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
                  The case for action this year
                </h2>
                <ul className="mt-6 space-y-2 text-sm md:text-base text-slate-700" data-testid="sustainability-why-now">
                  {content.why_now.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-emerald-600 mt-1 flex-none"/> <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Regulations summary */}
          {content.regulations_summary && (
            <section className="py-10 md:py-14 bg-white">
              <div className="max-w-3xl mx-auto px-5 md:px-8">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Regulations</div>
                <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
                  What you must comply with in {content.province_name}
                </h2>
                <p className="mt-3 text-sm md:text-base text-slate-700 leading-relaxed" data-testid="sustainability-regulations">
                  {content.regulations_summary}
                </p>
                {content.applicable_regulations?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm text-slate-700">
                    {content.applicable_regulations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-none mt-1"/> {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          {/* Savings levers */}
          {Array.isArray(content.savings_levers) && content.savings_levers.length > 0 && (
            <section className="py-10 md:py-14 bg-slate-50 border-y border-slate-200">
              <div className="max-w-5xl mx-auto px-5 md:px-8">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Top retrofits</div>
                <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
                  Where the money is
                </h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sustainability-levers">
                  {content.savings_levers.map((l, i) => (
                    <div key={i} className="p-4 rounded-2xl bg-white border border-emerald-200">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-display font-bold text-sm text-slate-900">{l.title}</div>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          {l.payback_years} yr payback
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Annual savings: <span className="font-bold text-emerald-700">${(l.annual_savings_cad || 0).toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-slate-700 mt-2">{l.why}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* FAQ */}
          {Array.isArray(content.faqs) && content.faqs.length > 0 && (
            <section className="py-12 md:py-20">
              <div className="max-w-3xl mx-auto px-5 md:px-8">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">FAQs — {content.province_name}</div>
                <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">Quick answers</h2>
                <div className="mt-6 space-y-3" data-testid="sustainability-faqs">
                  {content.faqs.map((f, i) => (
                    <details key={i} className="p-4 rounded-xl border border-slate-200 bg-white group">
                      <summary className="font-display font-bold cursor-pointer text-slate-900 flex items-start justify-between gap-3">
                        <span>{f.q}</span>
                        <ArrowRight className="w-4 h-4 text-emerald-600 flex-none mt-1 group-open:rotate-90 transition-transform"/>
                      </summary>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ─── Lead-magnet Health Check ─────────────────────────────────────────────
function HealthCheckPane({ province, provinceName }) {
  const [sqft, setSqft] = useState("");
  const [units, setUnits] = useState("1");
  const [yearBuilt, setYearBuilt] = useState("");
  const [hasGas, setHasGas] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const shield = useBotShield();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await fetch("/api/sustainability/health-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          sqft: Number(sqft),
          units: Number(units || 1),
          province,
          year_built: Number(yearBuilt),
          has_natural_gas: hasGas,
          ...shield.values(),
        }),
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      setResult(await r.json());
    } catch (e2) {
      setError(String(e2?.message || e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-10 md:py-14 bg-gradient-to-br from-violet-50 via-white to-emerald-50 border-y border-slate-200" id="health-check">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-violet-700 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3"/> Free · 30 seconds
        </div>
        <h2 className="font-display mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
          Get your {provinceName} sustainability score
        </h2>
        <p className="mt-3 text-sm text-slate-700">
          Plug in your building details — Mark calibrates your band + surfaces the 3 biggest savings levers.
        </p>

        {!result ? (
          <form onSubmit={submit} className="mt-6 bg-white border border-violet-200 rounded-2xl p-5 space-y-3" data-testid="sustainability-healthcheck-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Email (we'll email the full report)" value={email} onChange={setEmail} type="email" testid="healthcheck-email" required/>
              <Field label="Year built" value={yearBuilt} onChange={setYearBuilt} type="number" testid="healthcheck-year" required/>
              <Field label="Total sqft" value={sqft} onChange={setSqft} type="number" testid="healthcheck-sqft" required/>
              <Field label="Units (residential)" value={units} onChange={setUnits} type="number" testid="healthcheck-units"/>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={hasGas} onChange={(e) => setHasGas(e.target.checked)}
                data-testid="healthcheck-gas"
                className="rounded border-slate-300"/>
              Building has natural gas heating
            </label>
            {error && <div className="text-xs text-rose-700 p-2 rounded bg-rose-50 border border-rose-200">{error}</div>}
            <div className="flex justify-end">
              <button type="submit" disabled={busy}
                data-testid="healthcheck-submit"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
                Get my score
              </button>
            </div>
          </form>
        ) : (
          <HealthCheckResult result={result}/>
        )}
      </div>
    </section>
  );
}

function HealthCheckResult({ result }) {
  const bandTone = {
    A: "from-emerald-500 to-emerald-700", B: "from-sky-500 to-sky-700",
    C: "from-amber-500 to-amber-700", D: "from-rose-500 to-rose-700",
  }[result.band] || "from-slate-500 to-slate-700";
  return (
    <div className="mt-6 space-y-4" data-testid="healthcheck-result">
      <div className={`p-5 rounded-2xl bg-gradient-to-br ${bandTone} text-white`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Your sustainability band</div>
            <div className="font-display text-5xl font-bold tabular-nums" data-testid="healthcheck-band">{result.band}</div>
            <div className="text-sm opacity-90 mt-1">Score: {result.score} / 100</div>
          </div>
          <div className="text-right text-sm leading-relaxed">
            <div>EUI: <strong>{result.estimated_eui} kWh/sqft</strong></div>
            <div className="opacity-90">{result.estimated_emissions_tco2e} tCO₂e / year</div>
          </div>
        </div>
        <p className="mt-3 text-sm opacity-90">{result.headline}</p>
      </div>

      {result.biggest_lever && (
        <div className="p-3 rounded-xl bg-violet-50 border border-violet-200">
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Biggest lever</div>
          <p className="text-sm font-display font-bold text-slate-900 mt-1">{result.biggest_lever}</p>
        </div>
      )}

      {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-2">3 free recommendations</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {result.recommendations.map((r, i) => (
              <div key={i} className="p-3 rounded-xl bg-white border border-emerald-200">
                <div className="font-display font-bold text-sm">{r.title}</div>
                <div className="text-[11px] text-slate-500 mt-1">
                  ${(r.annual_savings_cad || 0).toLocaleString()}/yr · {r.payback_years} yr payback
                </div>
                <p className="text-xs text-slate-700 mt-1.5">{r.why}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.regulation_alert && (
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-none mt-0.5"/>
          <div>{result.regulation_alert}</div>
        </div>
      )}

      <div className="p-4 rounded-2xl bg-slate-900 text-white text-center">
        <Sparkles className="w-6 h-6 text-emerald-400 mx-auto"/>
        <h3 className="font-display text-lg font-bold mt-2">Want the full retrofit roadmap?</h3>
        <p className="text-xs text-slate-300 mt-1">We&apos;ll email it to you within 2 minutes.</p>
        <Link to="/signup" className="inline-flex items-center gap-1.5 mt-3 px-5 py-2 rounded-full bg-emerald-500 text-slate-900 font-bold text-sm hover:bg-emerald-400">
          Create free account <ArrowRight className="w-3.5 h-3.5"/>
        </Link>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 focus:border-violet-500 outline-none text-sm"/>
    </div>
  );
}
